package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.core.util.WeekRange
import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.CompanyType
import br.com.vvrefeicoes.portal.domain.model.EntryRow
import br.com.vvrefeicoes.portal.domain.model.EntryRowLabel
import br.com.vvrefeicoes.portal.domain.model.Installment
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.OperationalEntry
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryCatalog
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryKind
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryStatus
import br.com.vvrefeicoes.portal.domain.model.OperationalSupplier
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.Session
import br.com.vvrefeicoes.portal.domain.model.adminOthersPercentFor
import br.com.vvrefeicoes.portal.domain.model.allows
import br.com.vvrefeicoes.portal.domain.model.allowsAny
import br.com.vvrefeicoes.portal.domain.model.approved
import br.com.vvrefeicoes.portal.domain.model.cleared
import br.com.vvrefeicoes.portal.domain.model.rejected
import br.com.vvrefeicoes.portal.domain.model.responsibleNames
import br.com.vvrefeicoes.portal.domain.model.saved
import br.com.vvrefeicoes.portal.domain.model.submitted
import br.com.vvrefeicoes.portal.domain.model.unidadeKeyOf
import br.com.vvrefeicoes.portal.domain.model.updatedRow
import br.com.vvrefeicoes.portal.domain.model.withSupplierRow
import br.com.vvrefeicoes.portal.domain.model.withWeek
import br.com.vvrefeicoes.portal.domain.model.withoutRow
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.OperationalEntryRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import br.com.vvrefeicoes.portal.domain.repository.SupplierRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_approved_success
import portalvv.shared.generated.resources.operational_rejected_success
import portalvv.shared.generated.resources.operational_save_error
import portalvv.shared.generated.resources.operational_saved
import portalvv.shared.generated.resources.operational_submitted_success

enum class SupplierDialogMode {
    SELECT,
    NEW,
}

private const val AUTOSAVE_DEBOUNCE_MS = 500L

class OperationalEntriesController(
    private val repository: OperationalEntryRepository,
    private val supplierRepository: SupplierRepository,
    private val orgChartRepository: OrgChartRepository,
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope,
    private val kind: OperationalEntryKind,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var selectedCompany by mutableStateOf<CompanyRef?>(null)
        private set

    var period by mutableStateOf(dateTimeProvider.currentPeriod())
        private set

    var entry by mutableStateOf<OperationalEntry?>(null)
        private set

    var message by mutableStateOf<StringResource?>(null)
        private set

    var toastMessage by mutableStateOf<StringResource?>(null)

    /** Incrementado após enviar/aprovar/rejeitar, para telas dependentes recarregarem (ex.: pendências). */
    var refreshToken by mutableStateOf(0)
        private set

    var canApprove by mutableStateOf(false)
        private set

    var canEdit by mutableStateOf(false)
        private set

    var editMode by mutableStateOf(false)
        private set

    var cancelConfirmOpen by mutableStateOf(false)
        private set

    var submitConfirmOpen by mutableStateOf(false)
        private set

    var rejectOpen by mutableStateOf(false)
        private set

    var rejectReason by mutableStateOf("")
        private set

    var availableSuppliers by mutableStateOf<List<OperationalSupplier>>(emptyList())
        private set

    var supplierDialogOpen by mutableStateOf(false)
        private set

    var supplierDialogCategoryId by mutableStateOf<String?>(null)
        private set

    var supplierDialogRowId by mutableStateOf<String?>(null)
        private set

    var supplierDialogSupplierId by mutableStateOf<String?>(null)
        private set

    var supplierDialogNewName by mutableStateOf("")
        private set

    var supplierDialogMode by mutableStateOf(SupplierDialogMode.SELECT)
        private set

    var supplierDialogInstallment by mutableStateOf(false)
        private set

    var supplierDialogInstallments by mutableStateOf("")
        private set

    var supplierDialogCurrent by mutableStateOf("")
        private set

    var removeSupplierRowId by mutableStateOf<String?>(null)
        private set

    var clearConfirmOpen by mutableStateOf(false)
        private set

    var clearToken by mutableStateOf(0)
        private set

    var responsibleDirectorNames by mutableStateOf<List<String>>(emptyList())
        private set

    var responsibleManagerNames by mutableStateOf<List<String>>(emptyList())
        private set

    private var savedEntry by mutableStateOf<OperationalEntry?>(null)
    private var autosaveJob: Job? = null

    private var orgChartData: OrgChartData? = null
    private var adminOthersCompanies: List<Company> = emptyList()

    private var session: Session? = null
    private var started = false

    /** Edição de um lançamento aprovado: cria a nova versão (rascunho) só no primeiro salvamento. */
    private var pendingFork = false

    val weeks: List<WeekRange>
        get() = MonthCalendar.weeksOf(period)

    val editable: Boolean
        get() = entry?.editable == true

    val editing: Boolean
        get() = canEdit && editMode && editable

    val supportsApproval: Boolean
        get() = kind == OperationalEntryKind.GESTAO_FINANCEIRA

    val hasUnsavedChanges: Boolean
        get() = editing && entry != savedEntry

    val canReview: Boolean
        get() = canApprove && entry?.status == OperationalEntryStatus.SUBMITTED

    val supplierDialogIsEdit: Boolean
        get() = supplierDialogRowId != null

    val canConfirmSupplier: Boolean
        get() =
            when (supplierDialogMode) {
                SupplierDialogMode.SELECT -> supplierDialogSupplierId != null
                SupplierDialogMode.NEW -> supplierDialogNewName.isNotBlank()
            }

    val supplierOptions: List<String>
        get() {
            val used = usedSupplierIds()
            return availableSuppliers.filter { it.id !in used }.map { it.name }
        }

    suspend fun start(
        company: CompanyRef?,
        authorizedUnits: List<CompanyRef>,
    ) {
        loadError.loadGuarded {
            if (!started) {
                started = true
                session = authRepository.session.value
                orgChartData = orgChartRepository.loadInitial().currentVersion.data
                adminOthersCompanies = orgChartData?.companies.orEmpty()
                val permissions = session?.permissions.orEmpty()
                canEdit = permissions.allows(PermissionKeys.OPERATIONAL_ENTRIES, PermissionAction.EDIT)
                canApprove = supportsApproval && permissions.allowsAny(PermissionKeys.OPERATIONAL_ENTRIES_APPROVE)
            }
            val scoped = company?.takeIf { candidate -> authorizedUnits.any { it.name == candidate.name } }
            if (scoped != selectedCompany) {
                selectedCompany = scoped
                reload()
            }
        }
        loading = false
    }

    fun applyPeriod(month: MonthPeriod) {
        if (period == month) return
        period = month
        if (started) scope.launch { loadError.loadGuarded { reload() } }
    }

    fun startEdit() {
        if (!canEdit || !editable) return
        val current = entry ?: return
        // Editar um lançamento aprovado cria uma nova versão (rascunho). Isso é adiado para o
        // primeiro salvamento (automático ao mudar um valor ou pelo botão), e não ao entrar em edição.
        pendingFork = current.status == OperationalEntryStatus.APPROVED
        editMode = true
    }

    fun requestCancelEdit() {
        if (hasUnsavedChanges) {
            cancelConfirmOpen = true
        } else {
            exitEdit()
        }
    }

    fun confirmCancelEdit() {
        cancelConfirmOpen = false
        exitEdit()
    }

    fun dismissCancelEdit() {
        cancelConfirmOpen = false
    }

    fun requestSubmit() {
        val current = entry ?: return
        if (!canEdit) return
        if (!supportsApproval) return
        if (!current.editable) return
        submitConfirmOpen = true
    }

    fun confirmSubmit() {
        submitConfirmOpen = false
        submit()
    }

    fun dismissSubmit() {
        submitConfirmOpen = false
    }

    fun updateWeek(
        rowId: String,
        weekIndex: Int,
        cents: Long?,
    ) {
        val current = entry ?: return
        if (!editing) return
        entry = current.withWeek(rowId, weekIndex, cents)
    }

    fun requestClear() {
        if (!editing) return
        clearConfirmOpen = true
    }

    fun dismissClear() {
        clearConfirmOpen = false
    }

    fun confirmClear() {
        val current = entry ?: return
        if (!editing) return
        entry = current.cleared(weeks.size)
        clearConfirmOpen = false
        clearToken++
        autosave()
    }

    fun openAddSupplier(categoryId: String) {
        supplierDialogCategoryId = categoryId
        supplierDialogRowId = null
        supplierDialogSupplierId = null
        supplierDialogNewName = ""
        supplierDialogMode = SupplierDialogMode.SELECT
        supplierDialogInstallment = false
        supplierDialogInstallments = ""
        supplierDialogCurrent = ""
        supplierDialogOpen = true
        loadSuppliers(categoryId)
    }

    fun openEditSupplier(
        categoryId: String,
        row: EntryRow,
    ) {
        supplierDialogCategoryId = categoryId
        supplierDialogRowId = row.id
        supplierDialogSupplierId = row.supplierId
        supplierDialogNewName = ""
        supplierDialogMode = SupplierDialogMode.SELECT
        supplierDialogInstallment = row.installment != null
        supplierDialogInstallments =
            row.installment
                ?.total
                ?.toString()
                .orEmpty()
        supplierDialogCurrent =
            row.installment
                ?.current
                ?.toString()
                .orEmpty()
        supplierDialogOpen = true
        loadSuppliers(categoryId)
    }

    fun selectSupplier(supplierId: String) {
        supplierDialogSupplierId = supplierId
        supplierDialogNewName = ""
    }

    fun updateNewSupplierName(value: String) {
        supplierDialogNewName = value
        if (value.isNotBlank()) supplierDialogSupplierId = null
    }

    fun useNewSupplier() {
        supplierDialogMode = SupplierDialogMode.NEW
        supplierDialogSupplierId = null
        supplierDialogNewName = ""
    }

    fun useExistingSupplier() {
        supplierDialogMode = SupplierDialogMode.SELECT
        supplierDialogNewName = ""
    }

    fun updateInstallmentEnabled(enabled: Boolean) {
        supplierDialogInstallment = enabled
        if (!enabled) {
            supplierDialogInstallments = ""
            supplierDialogCurrent = ""
        }
    }

    fun updateInstallments(value: String) {
        supplierDialogInstallments = value.filter { it.isDigit() }
    }

    fun updateCurrentInstallment(value: String) {
        supplierDialogCurrent = value.filter { it.isDigit() }
    }

    fun confirmSupplierDialog() {
        val categoryId = supplierDialogCategoryId ?: return
        val current = entry ?: return
        if (!editing || !canConfirmSupplier) return
        scope.launch {
            val supplier =
                when (supplierDialogMode) {
                    SupplierDialogMode.NEW ->
                        supplierRepository.create(categoryId, supplierDialogNewName.trim()).also { created ->
                            availableSuppliers = availableSuppliers + created
                        }
                    SupplierDialogMode.SELECT -> availableSuppliers.firstOrNull { it.id == supplierDialogSupplierId }
                } ?: return@launch
            val installment =
                if (supplierDialogInstallment) {
                    resolveInstallment() ?: return@launch
                } else {
                    null
                }
            val rowId = supplierDialogRowId
            entry =
                if (rowId == null) {
                    current.withSupplierRow(
                        categoryId,
                        EntryRow(
                            id = "sup-${supplier.id}",
                            label = EntryRowLabel.Supplier(supplier.name),
                            supplierId = supplier.id,
                            installment = installment,
                        ),
                    )
                } else {
                    current.updatedRow(rowId) { row ->
                        row.copy(
                            label = EntryRowLabel.Supplier(supplier.name),
                            supplierId = supplier.id,
                            installment = installment,
                        )
                    }
                }
            dismissSupplierDialog()
            autosave()
        }
    }

    fun dismissSupplierDialog() {
        supplierDialogOpen = false
        supplierDialogCategoryId = null
        supplierDialogRowId = null
        supplierDialogSupplierId = null
        supplierDialogNewName = ""
        supplierDialogMode = SupplierDialogMode.SELECT
        supplierDialogInstallment = false
        supplierDialogInstallments = ""
        supplierDialogCurrent = ""
    }

    fun requestRemoveSupplier(rowId: String) {
        removeSupplierRowId = rowId
    }

    fun dismissRemoveSupplier() {
        removeSupplierRowId = null
    }

    fun confirmRemoveSupplier() {
        val rowId = removeSupplierRowId ?: return
        val current = entry ?: return
        if (!editing) return
        entry = current.withoutRow(rowId)
        removeSupplierRowId = null
        autosave()
    }

    fun saveDraft() {
        val current = entry ?: return
        if (!canEdit) return
        scope.launch {
            try {
                val toSave = if (pendingFork) forkDraft(current) else current
                val persisted = repository.save(toSave.saved(actor(), dateTimeProvider.nowTimestamp()))
                pendingFork = false
                applyPersisted(persisted)
                toastMessage = Res.string.operational_saved
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                // Sem permissão (ex.: criar exige CREATE) ou falha de rede: avisa sem derrubar a tela.
                message = Res.string.operational_save_error
            }
        }
    }

    /** Salva automaticamente (com debounce) sem exibir mensagem; usado ao sair de um campo. */
    fun autosave() {
        if (!canEdit || !editing) return
        if (entry == savedEntry) return
        autosaveJob?.cancel()
        autosaveJob =
            scope.launch {
                delay(AUTOSAVE_DEBOUNCE_MS)
                val snapshot = entry ?: return@launch
                if (!canEdit || !editing || snapshot == savedEntry) return@launch
                val toSave = if (pendingFork) forkDraft(snapshot) else snapshot
                val persisted =
                    runCatching {
                        repository.save(toSave.saved(actor(), dateTimeProvider.nowTimestamp()))
                    }.getOrNull() ?: return@launch
                pendingFork = false
                val current = entry
                if (current == null || current == snapshot) {
                    applyPersisted(persisted)
                } else {
                    // Houve edições novas enquanto o save estava em andamento. Preserva os valores
                    // locais e adota apenas a identidade/metadados do servidor, evitando que a
                    // resposta sobrescreva o que o usuário acabou de digitar (somas desatualizadas).
                    savedEntry = snapshot.withServerIdentity(persisted)
                    entry = current.withServerIdentity(persisted)
                }
            }
    }

    fun submit() {
        val current = entry ?: return
        if (!canEdit) return
        if (!supportsApproval) return
        if (!current.editable) return
        scope.launch {
            loadError.loadGuarded {
                val toSave = if (pendingFork) forkDraft(current) else current
                val saved = repository.save(toSave.saved(actor(), dateTimeProvider.nowTimestamp()))
                val persisted = repository.submit(saved.submitted(actor(), dateTimeProvider.nowTimestamp()))
                pendingFork = false
                editMode = false
                applyPersisted(persisted)
                message = Res.string.operational_submitted_success
                refreshToken++
            }
        }
    }

    fun approve() {
        val current = entry ?: return
        if (!supportsApproval) return
        if (current.status != OperationalEntryStatus.SUBMITTED) return
        scope.launch {
            loadError.loadGuarded {
                val persisted = repository.approve(current.approved(actor(), dateTimeProvider.nowTimestamp()))
                editMode = false
                applyPersisted(persisted)
                message = Res.string.operational_approved_success
                refreshToken++
            }
        }
    }

    fun openReject() {
        rejectOpen = true
    }

    fun cancelReject() {
        rejectOpen = false
        rejectReason = ""
    }

    fun updateRejectReason(value: String) {
        rejectReason = value
    }

    fun confirmReject() {
        val current = entry ?: return
        if (!supportsApproval) return
        if (current.status != OperationalEntryStatus.SUBMITTED) return
        scope.launch {
            loadError.loadGuarded {
                val reason = rejectReason.trim()
                val persisted = repository.reject(current.rejected(actor(), dateTimeProvider.nowTimestamp(), reason), reason)
                rejectOpen = false
                rejectReason = ""
                editMode = false
                applyPersisted(persisted)
                message = Res.string.operational_rejected_success
                refreshToken++
            }
        }
    }

    fun dismissMessage() {
        message = null
    }

    private fun resolveInstallment(): Installment? {
        if (!supplierDialogInstallment) return null
        val total = supplierDialogInstallments.toIntOrNull() ?: return null
        if (total <= 0) return null
        val currentInstallment = supplierDialogCurrent.toIntOrNull() ?: 0
        return Installment(total = total, current = currentInstallment.coerceIn(0, total))
    }

    private fun usedSupplierIds(): Set<String> {
        val categoryId = supplierDialogCategoryId ?: return emptySet()
        val currentRowId = supplierDialogRowId
        val table = entry?.tables?.firstOrNull { it.id == categoryId } ?: return emptySet()
        return table.rows
            .filter { it.id != currentRowId }
            .mapNotNull { it.supplierId }
            .toSet()
    }

    private fun loadSuppliers(categoryId: String) {
        scope.launch {
            val suppliers = supplierRepository.loadByCategory(categoryId)
            availableSuppliers = suppliers
            if (supplierDialogOpen && supplierDialogRowId == null && suppliers.isEmpty()) {
                supplierDialogMode = SupplierDialogMode.NEW
            }
        }
    }

    private fun exitEdit() {
        editMode = false
        scope.launch { loadError.loadGuarded { reload() } }
    }

    private fun applyPersisted(persisted: OperationalEntry) {
        val adjusted = persisted.copy(taxesExempt = isTaxExempt(selectedCompany))
        entry = adjusted
        savedEntry = adjusted
    }

    /** Nova versão (rascunho) de um lançamento aprovado, criada no primeiro salvamento da edição. */
    private fun forkDraft(current: OperationalEntry): OperationalEntry =
        current.copy(
            id = "new",
            status = OperationalEntryStatus.DRAFT,
            versao = current.versao + 1,
            oficial = false,
            submittedBy = "",
            submittedAt = null,
            reviewedBy = "",
            reviewedAt = null,
            rejectionReason = "",
            history = emptyList(),
            updatedBy = "",
            updatedAt = "",
        )

    private suspend fun reload() {
        editMode = false
        pendingFork = false
        val company = selectedCompany
        if (company == null) {
            entry = null
            savedEntry = null
            responsibleDirectorNames = emptyList()
            responsibleManagerNames = emptyList()
            return
        }
        val unidadeId = unidadeIdFor(company)
        val existing =
            repository
                .load(listOf(unidadeId), period, kind)
                .firstOrNull()
                ?.copy(companyId = company.id, companyName = company.name)
        val loaded = existing ?: newEntry(company, unidadeId)
        val withTax = loaded.copy(taxesExempt = isTaxExempt(company))
        entry = withTax
        savedEntry = withTax
        val names = orgChartData?.responsibleNames(company.name)
        responsibleDirectorNames = names?.first.orEmpty()
        responsibleManagerNames = names?.second.orEmpty()
    }

    private fun unidadeIdFor(company: CompanyRef): String = orgChartData?.unidadeKeyOf(company) ?: company.id.toString()

    /** Unidades do tipo "Lojinha" não possuem imposto (a linha permanece, mas zerada). */
    private fun isTaxExempt(company: CompanyRef?): Boolean =
        company?.let { ref ->
            adminOthersCompanies.firstOrNull { it.name.equals(ref.name, ignoreCase = true) }?.type == CompanyType.LOJ
        } ?: false

    private fun newEntry(
        company: CompanyRef,
        unidadeId: String,
    ): OperationalEntry =
        OperationalEntryCatalog.newEntry(
            companyId = company.id,
            companyName = company.name,
            period = period,
            weekCount = weeks.size,
            kind = kind,
            adminOthersPercent = adminOthersCompanies.adminOthersPercentFor(company.name),
            unidadeId = unidadeId,
        )

    private fun actor(): String = session?.displayName.orEmpty()
}

/** Mantém o conteúdo local e adota apenas a identidade/metadados retornados pelo servidor. */
private fun OperationalEntry.withServerIdentity(server: OperationalEntry): OperationalEntry =
    copy(
        id = server.id,
        versao = server.versao,
        status = server.status,
        oficial = server.oficial,
        updatedBy = server.updatedBy,
        updatedAt = server.updatedAt,
        submittedBy = server.submittedBy,
        submittedAt = server.submittedAt,
        reviewedBy = server.reviewedBy,
        reviewedAt = server.reviewedAt,
        rejectionReason = server.rejectionReason,
        history = server.history,
    )

@Composable
fun rememberOperationalEntriesController(kind: OperationalEntryKind): OperationalEntriesController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember(kind) {
        OperationalEntriesController(
            repository = appGraph.operationalEntryRepository,
            supplierRepository = appGraph.supplierRepository,
            orgChartRepository = appGraph.orgChartRepository,
            authRepository = appGraph.authRepository,
            scope = scope,
            kind = kind,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}
